import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormContext } from "react-hook-form";
import PropTypes from "prop-types";

import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";

import DialogPopup from "../../../DialogPopup";
import LocalLoader from "../../../Loaders/Local";
import { hideFormDialog } from "../../context/dialogSlice";
import Styled from "./styles";

import { methodRefs, addressOptionsCache } from "../../context/formSlice";
import { notifyError } from "../../../../utils/helpers";
import { getAddressFieldKey } from "../../services/form.service";
import { showNotification } from "../../../../features/core/slices/notification.slice";
import { useVerifyAddressMutation } from "../../../../api/forms.api.slice";
import AddressCard from "./AddressCard";
import {
  checkFieldValidity,
  updateGeoCodes,
} from "../../services/form.dialog.service";

const getDisplayValue = (key, value) => {
  const optionsCacheKey = getAddressFieldKey(key);
  const options = optionsCacheKey && addressOptionsCache[optionsCacheKey];
  if (!options) return value;
  return options.find((item) => item.value === value)?.label || value;
};

function VerifyAddress({ handleFormSave }) {
  const dispatch = useDispatch();
  const {
    setValue: formSetValue,
    trigger: formTrigger,
    getFieldState: formGetFieldState,
  } = useFormContext();
  const [selectedItem, setSelectedItem] = useState(0);

  const {
    formDialog: { fieldProps, userEnteredAddress, datagridKey, recordId },
  } = useSelector((state) => state.formDialog);

  const [verifyAddress, { data: verifiedAddress, isLoading, error, isError }] =
    useVerifyAddressMutation();

  const { key: buttonKey, properties = {} } = fieldProps || {};
  const { hasGeoCode, sourceKey } = properties;
  const loading = isLoading;

  const isDg = Boolean(datagridKey && recordId);
  const dgMethods = isDg ? methodRefs[`${datagridKey}${recordId}`] : null;
  // form methods
  const setValue = isDg ? dgMethods.setValue : formSetValue;
  const trigger = isDg ? dgMethods.trigger : formTrigger;
  const getFieldState = isDg ? dgMethods.getFieldState : formGetFieldState;

  useEffect(() => {
    const getVerifiedAddress = async (payload) => {
      try {
        await verifyAddress(payload).unwrap();
      } catch (err) {
        notifyError(dispatch, showNotification, err);
      }
    };
    if (userEnteredAddress && userEnteredAddress.length > 0) {
      const payload = userEnteredAddress.reduce(
        (acc, curr) => ({ ...acc, [curr.key]: curr.value }),
        {}
      );
      if (hasGeoCode) payload.hasGeoCode = true;
      getVerifiedAddress(payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEnteredAddress]);

  const handleAddressSelection = (index) => {
    if (index !== selectedItem) setSelectedItem(index);
  };

  const closeDialog = () => {
    dispatch(hideFormDialog());
  };

  const handleKeyDown = (e, i) => {
    if (e.key === "Enter" || e.key === " ") {
      handleAddressSelection(i);
    }
  };

  const updateVerificationStatus = (isValidField) => {
    setValue(`${buttonKey}Verified`, isValidField);
    trigger(`${buttonKey}Verified`);
  };

  const dispatchNotification = (type, msg) => {
    dispatch(showNotification({ type, msg, show: true }));
  };

  const handleValidationError = () => {
    updateVerificationStatus(false);
    dispatchNotification("error", "Something went wrong. Please try again.");
  };

  const setFieldValuesAndTrigger = (fields, valueSource) => {
    const promises = fields.map(async (field) => {
      setValue(field.key, valueSource(field));
      return trigger(field.key);
    });
    if (hasGeoCode && sourceKey && verifiedAddress)
      updateGeoCodes(sourceKey, verifiedAddress, setValue);
    return promises;
  };

  const updateAddress = async () => {
    let validationPromises;
    if (selectedItem === 1) {
      validationPromises = setFieldValuesAndTrigger(
        userEnteredAddress,
        (field) => field.value
      );
    } else if (selectedItem === 2) {
      validationPromises = setFieldValuesAndTrigger(
        userEnteredAddress,
        (field) => verifiedAddress[field.key]
      );
    }
    try {
      await Promise.all(validationPromises);
      const isValidField = checkFieldValidity(
        userEnteredAddress,
        getFieldState
      );
      updateVerificationStatus(isValidField);
      if (isValidField) handleFormSave?.();
      else dispatchNotification("warning", "Please select a valid address.");
    } catch {
      handleValidationError();
    } finally {
      closeDialog();
    }
  };

  const modalContent = (
    <Styled dialogState={{ isError, selectedItem }}>
      <Grid
        container
        className="gridContainer"
        columnSpacing={2}
        rowSpacing={2}
      >
        <Grid>
          <Alert role="status" severity="info" tabIndex={0}>
            Please select the address you wish to update in the application by
            clicking on the respective section.
          </Alert>
        </Grid>
        <Grid size={{ xs: 12, sm: 5.7 }}>
          <AddressCard
            title="Entered Address"
            onKeyDown={handleKeyDown}
            isSelected={selectedItem === 1}
            onAddressSelection={handleAddressSelection}
            content={(userEnteredAddress || []).map((field) => (
              <Typography key={field.key} variant="body1">
                {getDisplayValue(field.key, field.value)}
              </Typography>
            ))}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 5.7 }}>
          <AddressCard
            isVerifiedAddress
            title="Verified Address"
            onKeyDown={handleKeyDown}
            isSelected={selectedItem === 2}
            onAddressSelection={handleAddressSelection}
            content={
              verifiedAddress &&
              (userEnteredAddress || []).map((field) => (
                <Typography key={field.key} variant="body1">
                  {getDisplayValue(field.key, verifiedAddress[field.key])}
                </Typography>
              ))
            }
            error={error}
          />
        </Grid>
      </Grid>
      <LocalLoader progress={loading} />
    </Styled>
  );

  const buttonArray = [
    {
      id: 1,
      name: "Cancel",
      variant: "contained",
      onClickHandler: closeDialog,
      show: true,
      disabled: loading,
    },
    {
      id: 2,
      name: "Done",
      variant: "contained",
      onClickHandler: updateAddress,
      show: true,
      disabled: !selectedItem || loading,
    },
  ];

  return (
    <DialogPopup
      title="Address Selection"
      content={modalContent}
      buttonArray={buttonArray}
      openPopup
      onCancel={undefined}
      showActionBtn
      maxWidth="md"
      hideIcon="none"
    />
  );
}
VerifyAddress.propTypes = {
  handleFormSave: PropTypes.func,
};
VerifyAddress.defaultProps = {
  handleFormSave: undefined,
};
export default VerifyAddress;
